import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {Subscription, combineLatest} from "rxjs"
import {Three as THREE} from "@cm/material-nodes/three"
import {SceneNodes, SceneNodePart, objectPathStartsWith} from "@cm/template-nodes"

export abstract class ThreeAnnotationBase extends THREE.Object3D {
    private elementCache = new Map<string, HTMLDivElement>()
    private subscription: Subscription | undefined

    constructor(
        protected threeSceneManagerService: ThreeSceneManagerService,
        protected sceneNodePart: SceneNodePart,
    ) {
        super()
    }

    updateSceneNode(sceneNode: SceneNodes.SceneNode) {
        this.sceneNodePart.sceneNode = sceneNode
    }

    protected init() {
        this.subscription = combineLatest([
            this.threeSceneManagerService.sceneManagerService.selectedObjectPathParts$,
            this.threeSceneManagerService.sceneManagerService.hoveredObjectPathParts$,
        ]).subscribe(([selectedNodeParts, hoveredNodeParts]) => {
            if (this.threeSceneManagerService.$displayMode() === "configurator") return

            const outlineObjects = hoveredNodeParts.length > 0 ? hoveredNodeParts : selectedNodeParts

            const highlighted = outlineObjects.some((nodePart) => {
                return (
                    objectPathStartsWith(this.sceneNodePart.sceneNode.path, nodePart.path) &&
                    (this.sceneNodePart.part === nodePart.part || nodePart.part === "root")
                )
            })

            const selected = selectedNodeParts.some((nodePart) => {
                return objectPathStartsWith(this.sceneNodePart.sceneNode.path, nodePart.path) && this.sceneNodePart.part === nodePart.part
            })

            this.updateStyle(highlighted, selected)
        })
    }

    protected abstract updateStyle(highlighted: boolean, selected: boolean): void

    abstract isConcealable(): boolean
    abstract updateLabels(label: string, description: string): void
    abstract getTranslationOffset(element: HTMLDivElement): {x: number; y: number}
    abstract getOcclusionOpacity(): number

    getElement(camera: THREE.Camera) {
        const cached = this.elementCache.get(camera.uuid)
        if (cached) return cached
        else {
            const cloned = this.getContainer().cloneNode(true) as HTMLDivElement

            cloned.addEventListener("mousedown", this.onMouseDown)
            cloned.addEventListener("click", this.onClick)

            this.elementCache.set(camera.uuid, cloned)
            return cloned
        }
    }

    protected abstract getContainer(): HTMLDivElement

    private onMouseDown = (event: MouseEvent) => {
        event.stopPropagation()
    }

    protected onClick = (event: MouseEvent) => {
        if (this.threeSceneManagerService.$displayMode() === "configurator") return

        if (this.parent) {
            this.threeSceneManagerService.sceneManagerService.handleClickEvent({
                target: [{sceneNodePart: this.sceneNodePart}],
                modifiers: {
                    shiftKey: event.shiftKey,
                    ctrlKey: event.ctrlKey,
                    metaKey: event.metaKey,
                },
            })
            event.stopPropagation()
        }
    }

    dispose(final: boolean) {
        for (const element of this.elementCache.values()) {
            element.removeEventListener("mousedown", this.onMouseDown)
            element.removeEventListener("click", this.onClick)
            element.remove()
        }
        this.elementCache.clear()

        if (final) this.subscription?.unsubscribe()
    }
}
